import { useEffect, useMemo, useState } from "react";
import { replaceSingleBracketWithString } from "../hooks/replaceKeyWithNumber";
import { extractPercentageStrings } from "../hooks/useRegex";
import Discount from "./Discount";
import Filter from "./Filter";
import Modal from "./Modal";
import Table from "./Table";
import AISearchEngine from "./AISearchEngine";
import SeoHead from "../components/SeoHead";

// Decode entities like &lt;li&gt; → <li> on the client only
const decodeHtml = (str = "") => {
  if (typeof window === "undefined") return str; // skip during SSR
  const txt = document.createElement("textarea");
  txt.innerHTML = str;
  return txt.value;
};

export default function Search({
  translation,
  store,
  topStores,
  discounts,
  subdomain: initialSubdomain,
  domain,
  countryCode,
  canonicalUrl,
  currentEvent,
  isEventPage,
}) {
  const itemsLimitOnPage = 10;

  const [activePage, setActivePage] = useState(0);
  const [filteredOffers, setFilteredOffers] = useState(discounts || []);
  const [allOffers, setAllOffers] = useState(discounts || []);
  const [showModal, setShowModal] = useState(false);
  const [currentCard, setCurrentCard] = useState();
  const [pages, setPages] = useState(((discounts || []).length) / itemsLimitOnPage);
  const [error, setError] = useState(false);
  const [subdomain, setSubdomain] = useState(initialSubdomain || "");

  // --- safe HTML strings ---
  const decodeHtml = (str = "") => str.replace(/&lt;/g, "<").replace(/&gt;/g, ">").replace(/&amp;/g, "&").replace(/&quot;/g, '"').replace(/&#39;/g, "'");
  const stripHtml  = (str = "") => str.replace(/<[^>]*>/g, "");
  const safeMetaTitle = stripHtml(decodeHtml(store?.meta_title || "")).trim();
  const safeMetaDescr = stripHtml(decodeHtml(store?.meta_description || "")).trim();
  const safeMetaH1 = store?.meta_h1 ? decodeHtml(store.meta_h1) : "";
  const safeMetaH2 = store?.meta_h2 ? decodeHtml(store.meta_h2) : "";
  const safeBottom = store?.contentbottom ? decodeHtml(store.contentbottom) : "";

  // Compute latest offer date for "bijgewerkt op" display
  const latestOfferDate = (() => {
    if (!discounts || !Array.isArray(discounts) || discounts.length === 0) return null;
    const dates = discounts
      .map(o => o.startDate || '')
      .filter(Boolean)
      .sort();
    return dates.length > 0 ? dates[dates.length - 1] : null;
  })();

  const formattedOfferDate = (() => {
    if (!latestOfferDate) return null;
    try {
      const locale = translation?.locale || 'nl-NL';
      return new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'long', year: 'numeric' }).format(new Date(latestOfferDate));
    } catch {
      return latestOfferDate;
    }
  })();

  // --- Debug logger (only when ?debug=1) ---
  const debugEnabled = useMemo(() => {
    if (typeof window === "undefined") return false;
    try {
      const p = new URLSearchParams(window.location.search);
      return p.get("debug") === "1";
    } catch {
      return false;
    }
  }, []);

  const dbg = (...args) => {
    if (!debugEnabled) return;
    // eslint-disable-next-line no-console
    console.log("[Search]", ...args);
  };

  // --- Fix #1: keep state in sync when discounts change ---
  useEffect(() => {
    const list = Array.isArray(discounts) ? discounts : [];
    setAllOffers(list);
    setFilteredOffers(list);
    setActivePage(0);
    setPages(list.length / itemsLimitOnPage);

    dbg("discounts updated", {
      len: list.length,
      first: list[0],
      store: store ? { id: store.id, name: store.name, slug: store.slug } : null,
      initialSubdomain,
      countryCode,
      domain,
    });
  }, [discounts, store?.id]); // store?.id included so switching stores resets state too

  // --- Open modal if hash points to an offer id ---
  // Hash must be an integer (coupon ID). Non-integer hashes like #bottomtxt or #forward are ignored.
  const openModalFromHash = () => {
    if (typeof window === "undefined") return;

    const cid = window.location.hash?.slice(1);
    if (!cid) return;

    // Only open modal for integer hash values (coupon IDs)
    if (!/^\d+$/.test(cid)) return;

    const list = Array.isArray(discounts) ? discounts : [];
    if (!list.length) return;

    // Try exact match first
    let card = list.find((coupon) => String(coupon.id) === String(cid));
    if (!card) {
      // goid from ZeroPark may not match any coupon — use first available
      card = list[0];
      dbg("hash id not found, using first coupon", { cid, firstId: card.id });
    } else {
      dbg("opened modal via hash", { cid });
    }

    setCurrentCard(card);
    setShowModal(true);
  };

  // Fire on discounts change (SSR data ready)
  useEffect(() => {
    openModalFromHash();
    // Delayed retry — covers bfcache restore and Chrome prefetch where hash
    // isn't available yet when the effect first runs.
    const t = setTimeout(openModalFromHash, 300);
    return () => clearTimeout(t);
  }, [discounts]);

  // Listen for hashchange (client-side nav, e.g. clicking a #forward link)
  // and pageshow (bfcache restore from back/forward navigation)
  useEffect(() => {
    if (typeof window === "undefined") return;
    window.addEventListener("hashchange", openModalFromHash);
    const onPageShow = (e) => { if (e.persisted) openModalFromHash(); };
    window.addEventListener("pageshow", onPageShow);
    return () => {
      window.removeEventListener("hashchange", openModalFromHash);
      window.removeEventListener("pageshow", onPageShow);
    };
  }, [discounts]);

  // --- Redirect away if store missing + determine subdomain client-side fallback ---
  useEffect(() => {
    if (typeof window === "undefined") return;

    // WAIT until hydration finished
    if (store === undefined) return;

    // no redirect for temporary empty states
    if (!store || !domain) {
      dbg("store/domain missing", { store, domain });
      return;
    }

    if (!initialSubdomain) {
      const newSubdomain = window.location.hostname.split(".")[0];
      setSubdomain(newSubdomain);
      dbg("derived subdomain client-side", { newSubdomain });
    } else {
      setSubdomain(initialSubdomain);
    }
  }, [store, initialSubdomain, domain]);

  const activeClass = () => (pages - activePage > 1 ? "text-primary" : "text-gray-550");
  const goPrev = () => activePage !== 0 && setActivePage(activePage - 1);
  const goNext = () => pages - activePage > 1 && setActivePage(activePage + 1);
  // Use store logo for og:image on store pages; fall back to site logo
  const siteLogo = `https://www.${domain}/logos/logo-${countryCode}.png`;
  const image = store?.logoUrl || siteLogo;

  return (
    <>
      {translation && (
        store?.name ? (
          <>
            <SeoHead
                title={safeMetaTitle}
                description={safeMetaDescr}
                canonical={canonicalUrl}
                image={image}
                domain={domain}

                offers={allOffers}
                store={store}
                countryCode={countryCode}
              />
            {debugEnabled && (
              <div className="inner-container px-4 xl:px-0 mt-4">
                <div className="p-3 text-xs rounded border border-gray-200 bg-white">
                  <div><b>debug=1</b></div>
                  <div>store: {store?.name} (id: {store?.id}, slug: {store?.slug})</div>
                  <div>subdomain: {subdomain} (initial: {initialSubdomain || "-"})</div>
                  <div>discounts: {Array.isArray(discounts) ? discounts.length : 0}</div>
                  <div>allOffers: {allOffers.length}</div>
                  <div>filteredOffers: {filteredOffers.length}</div>
                  <div>activePage: {activePage} / pages: {pages}</div>
                  <div>countryCode: {countryCode || "-"}</div>
                  <div>canonicalUrl: {canonicalUrl || "-"}</div>
                </div>
              </div>
            )}

            <Modal
              variant="secondary"
              url={`/`}
              currentCard={currentCard}
              merchantDomain={store?.domain || ''}
              showModal={showModal}
              setShowModal={setShowModal}
              translation={translation}
              domain={domain}
              countryCode={countryCode}
            />

            <div className="w-full inner-container mt-0 md:mt-2 flex-col lg:flex-row flex lg:gap-12 px-4 xl:px-0">
              <Filter
                topStores={topStores}
                query={subdomain}
                setActivePage={setActivePage}
                error={error}
                allOffers={allOffers}
                setPages={setPages}
                store={store}
                filteredOffers={filteredOffers}
                setFilteredOffers={setFilteredOffers}
                translation={translation}
                currentEvent={currentEvent}
                domain={domain}
                countryCode={countryCode}
              />

              <div className="w-full max-w-[850px]">
                {store && (
                  <div>
                      <div className="flex items-center gap-4 lg:block">
                        <div className="bg-white max-w-24 p-4 lg:hidden">
                          <img
                            src={store?.logoUrl}
                            alt={`${store?.name} Logo`}
                            width="300"
                            height="240"
                            loading="lazy"
                          />
                        </div>
                    
                        <h1
                          className="title2 text-left text-gray-650"
                          dangerouslySetInnerHTML={{ __html: safeMetaH1 }}
                          suppressHydrationWarning
                        />
                      </div>
                    
                      <p className="text-[#707070]" suppressHydrationWarning>
                        <span dangerouslySetInnerHTML={{ __html: safeMetaH2 }} />
                        {safeBottom && (
                          <a href="#bottomtxt" className="preinfo-link">
                            <span>{translation?.bottom_anchor}</span>
                          </a>
                        )}
                      </p>

                    </div>
                )}

                {false && (
                  <AISearchEngine
                    storeName={store?.name || subdomain}
                    slug={store?.slug || initialSubdomain || subdomain}
                    countryCode={countryCode}
                    translation={translation}
                  />
                )}

                <div className="flex flex-col md:mt-4 mt-4 gap-3.5">
                  {filteredOffers
                    .slice(activePage * itemsLimitOnPage, (activePage + 1) * itemsLimitOnPage)
                    .map(({ id, activationUrl, merchantName, merchantSubdomain, merchantLogo, title, description, code, hits, offer_type }, index) => {
                      const discount = extractPercentageStrings(title)[0];
                      return (
                        <Discount
                          merchantName={merchantName}
                          merchantSubdomain={merchantSubdomain}
                          merchantDomain={store?.domain || ''}
                          code={code}
                          variant="small"
                          key={id ?? index}
                          query={subdomain}
                          url={activationUrl}
                          hits={hits}
                          id={id}
                          translation={translation}
                          discount={discount}
                          logo={merchantLogo}
                          title={title}
                          description={description}
                          offerType={offer_type}
                          domain={domain}
                          isEventPage={isEventPage}
                          onCodeClick={(card) => {
                            setCurrentCard(card);
                            setShowModal(true);
                          }}
                        />
                      );
                    })}
                </div>

                {filteredOffers.length >= 10 && (
                  <div className="md:mt-14 mt-5 flex items-center text-xs md:text-base gap-8 justify-center font-sans">
                    <button
                      onClick={goPrev}
                      className={activePage === 0 ? "text-gray-550" : "text-primary"}
                    >
                      {translation?.previous}
                    </button>
                    <p className="text-primary">{activePage + 1}</p>
                    <button onClick={goNext} className={activeClass()}>
                      {translation?.next}
                    </button>
                  </div>
                )}

                {filteredOffers.length > 0 && (
                  <div className="border-t border-gray-850 md:mt-12 mt-10 pt-10 border-dashed">
                    {store && (
                      <h2 className="title2 text-gray-650">
                        {replaceSingleBracketWithString(
                          translation?.couponpage_h3,
                          store?.name
                        )}
                      </h2>
                    )}
                
                    <Table
                      activePage={activePage}
                      filteredOffers={filteredOffers}
                      translation={translation}
                    />
                  </div>
                )}
                
                <div id="bottomtxt" className="seo-content" dangerouslySetInnerHTML={{ __html: safeBottom }} />
              </div>
            </div>
          </>
        ) : (
          <div className="p-4 text-center">
            <h2>{translation?.no_store_found || 'No such store found'}</h2>
          </div>
        )
      )}
    </>
  );
}